import React, { memo, useEffect, useRef, useState } from "react";
import { colors2 as colors } from "oolib";
import { DebugControls } from "../..";
import { CountVizLabels } from "../../../types";
import { BaseBarChartUIConfigs, LABEL_CSS_CLASSES } from "../../_base_barchart_ui_configs";
import { _Locale } from "@/components/locale/Locale";

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

export const formatPercentage = (percentage: number): string => {
  return `${parseFloat(percentage.toFixed(1))}%`;
};

const formatBarChartValue = ({
  value,
  isGroupedChart,
  isBreakdown,
  showPercent,
  showSumOfBreakdownValues,
  totalDatasetCount
}: {
  value: CountVizLabels | CountVizLabels[];
  isGroupedChart: boolean;
  isBreakdown: boolean;
  showPercent: boolean;
  showSumOfBreakdownValues: boolean;
  totalDatasetCount?: number;
}): string | number => {
  // Handle grouped charts - show individual bar value
  if (isGroupedChart) {
    const singleValue = value as CountVizLabels;
    return showPercent
      ? formatPercentage(singleValue.percentage)
      : singleValue.count;
  }

  // Handle breakdown charts
  if (isBreakdown) {
    const values = value as CountVizLabels[];

    // Show sum of all breakdown values
    if (showSumOfBreakdownValues) {
      const valueKey = showPercent ? 'percentage' : 'count';
      const totalValue = values.reduce((acc, item) => acc + item[valueKey], 0);
      return showPercent ? formatPercentage(totalValue) : totalValue;
    }

    // Show total for stacked charts relative to entire dataset
    const totalCount = values.reduce((sum, item) => sum + item.count, 0);
    if (showPercent && totalDatasetCount) {
      const groupPercentage = (totalCount / totalDatasetCount) * 100;
      return formatPercentage(groupPercentage);
    }
    return totalCount;
  }

  // Handle single value charts
  const singleValue = value as CountVizLabels;
  return showPercent
    ? formatPercentage(singleValue.percentage)
    : singleValue.count;
};

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type CustomizedLabelValue = CountVizLabels | CountVizLabels[];

interface CustomizedLabelProps {
  // Layout props
  y?: number | string;
  width?: number | string;
  height?: number | string;

  // Data props
  value?: CustomizedLabelValue;
  showPercent: boolean;
  showCount: boolean;
  opacity: number;
  activeBar?: string;
  highlightValue?: string; // datum `value` of the applied filter → persistent border + funnel icon
  breakdownDisplayType?: 'grouped' | 'stacked';
  stackIndex?: number;
  totalDatasetCount?: number;

  // Configuration props
  _base_barchart_ui_configs: BaseBarChartUIConfigs;
  _debug_controls: DebugControls;

  // Measurement props
  TOT_HEIGHT_SINGLE_BAR_SECTION: number;
  TOT_HEIGHT_STACKED_BAR_SECTION: number;
  TOT_HEIGHT_GROUPED_BAR_SECTION: number;
  CALC_SINGLE_BAR_SECTION_Y_POS: (index: number) => number;
  CALC_STACKED_BAR_SECTION_Y_POS: (index: number) => number;

  showSumOfBreakdownValues?: boolean;

  // Group label props
  groupData?: CountVizLabels[];

  // Width calculation props for alignment
  maxPercentWidth?: number;
  maxCountWidth?: number;

  // Event handlers
  onMouseOver?: (
    e: React.MouseEvent<SVGRectElement, MouseEvent>,
    v: CustomizedLabelValue
  ) => void;
  onMouseOut?: (
    e: React.MouseEvent<SVGRectElement, MouseEvent>,
    v: CustomizedLabelValue
  ) => void;
  onMouseDown?: (
    e: React.MouseEvent<SVGRectElement, MouseEvent>,
    v: CustomizedLabelValue
  ) => void;

  // Overflow handling (see BarChart): report this label's overflow state so
  // the breakdown tooltip can prepend the full label, and report row hover so
  // non-breakdown charts can show a full-label tooltip.
  onLabelOverflowChange?: (name: string, overflowing: boolean) => void;
  onOverflowLabelHover?: (name: string | null) => void;
}

// =============================================================================
// CUSTOM LABEL COMPONENT
// =============================================================================
// This component is wrapped with React.memo to prevent unnecessary re-renders.
// The parent (BarChartRenderer) pre-computes expensive props via useMemo/useCallback,
// so the shallow comparison in React.memo is effective.

const CustomizedLabelInner = ({
  y,
  width,
  height,
  value,
  showPercent,
  showCount,
  opacity,
  _base_barchart_ui_configs,
  _debug_controls,
  onMouseOver,
  onMouseOut,
  onMouseDown,
  activeBar,
  highlightValue,
  TOT_HEIGHT_SINGLE_BAR_SECTION,
  TOT_HEIGHT_STACKED_BAR_SECTION,
  TOT_HEIGHT_GROUPED_BAR_SECTION,
  CALC_SINGLE_BAR_SECTION_Y_POS,
  CALC_STACKED_BAR_SECTION_Y_POS,
  breakdownDisplayType,
  showSumOfBreakdownValues = false,
  stackIndex,
  totalDatasetCount,
  groupData,
  maxPercentWidth = 0,
  maxCountWidth = 0,
  onLabelOverflowChange,
  onOverflowLabelHover,
}: CustomizedLabelProps) => {
  // ---------------------------------------------------------------------------
  // LABEL OVERFLOW (hooks stay above the early returns)
  // ---------------------------------------------------------------------------
  // SVG version of the BarGraphCollection pattern (scrollWidth > clientWidth +
  // 30px mask-image fade): measure the name tspan against the svg width; when
  // it overflows, swap its fill for a gradient that fades out at the svg's
  // right edge. Runs after every render — label text and chart width both
  // change without a reliable dep, and the measurement is one DOM read.
  const nameTspanRef = useRef<SVGTSpanElement | null>(null);
  const gradientId = useRef(
    `barLabelFade-${Math.random().toString(36).slice(2)}`
  ).current;
  const [labelOverflow, setLabelOverflow] = useState({
    overflowing: false,
    svgWidth: 0,
  });
  useEffect(() => {
    const el = nameTspanRef.current;
    const svg = el?.ownerSVGElement;
    if (!el || !svg) return;
    const svgWidth = svg.clientWidth;
    let startX = 0;
    try {
      startX = el.getStartPositionOfChar(0).x;
    } catch {
      return; // empty text
    }
    const overflowing = startX + el.getComputedTextLength() > svgWidth;
    setLabelOverflow((prev) =>
      prev.overflowing === overflowing && prev.svgWidth === svgWidth
        ? prev
        : { overflowing, svgWidth }
    );
    onLabelOverflowChange?.(el.textContent || "", overflowing);
  });

  if (!value) return null;

  // Handle empty arrays (can happen when all segments are filtered/hidden)
  if (Array.isArray(value) && value.length === 0) return null;

  // ---------------------------------------------------------------------------
  // CHART TYPE IDENTIFICATION
  // ---------------------------------------------------------------------------
  const isGroupedChart = breakdownDisplayType === 'grouped' && stackIndex !== undefined;
  const isBreakdown = Array.isArray(value) && !isGroupedChart;
  const isStackedChart = isBreakdown && !showSumOfBreakdownValues && breakdownDisplayType === 'stacked';

  // The applied-filter bar: matched on the datum's `value`. Only ever passed for
  // single-value (non-breakdown) charts, where `value` is one labels object (not
  // an array). Gets a persistent border + funnel icon.
  const isHighlighted =
    highlightValue != null &&
    !Array.isArray(value) &&
    (value as CountVizLabels).value === highlightValue;

  // ---------------------------------------------------------------------------
  // DATA EXTRACTION
  // ---------------------------------------------------------------------------
  const name = isBreakdown
    ? value[0]?.name ?? ''
    : isGroupedChart
      ? (value as CountVizLabels).tooltipLabel || (value as CountVizLabels).name
      : (value as CountVizLabels).name;

  const formatValue = () => {
    return formatBarChartValue({
      value,
      isGroupedChart,
      isBreakdown,
      showPercent,
      showSumOfBreakdownValues,
      totalDatasetCount
    });
  };

  const getCount = () => {
    if (isGroupedChart) {
      return (value as CountVizLabels).count;
    } else if (isBreakdown) {
      return (value as CountVizLabels[]).reduce((sum, v) => sum + v.count, 0);
    } else {
      return (value as CountVizLabels).count;
    }
  };

  // ---------------------------------------------------------------------------
  // POSITIONING CALCULATIONS
  // ---------------------------------------------------------------------------
  const mainLabelX = _base_barchart_ui_configs.marginLeft + _base_barchart_ui_configs.labelLeftSpacing;
  const mainLabelDy = (y as number) - _base_barchart_ui_configs.labelToBarGap;

  const stackedLabelX = _base_barchart_ui_configs.marginLeft;
  const stackedLabelDy = (y as number) - (_base_barchart_ui_configs.stackedLabelToBarGap + _base_barchart_ui_configs.labelHeight);

  // Calculate label position based on what elements are shown
  const calculateLabelPosition = (baseX: number) => {
    if (!showPercent && !showCount) {
      return baseX; // No value/count shown, label starts at beginning
    }

    if (showPercent && showCount && (isGroupedChart || !isBreakdown || isStackedChart || showSumOfBreakdownValues)) {
      // Both percent and count shown as separate elements
      return baseX + maxPercentWidth + _base_barchart_ui_configs.labelElementGap + maxCountWidth + _base_barchart_ui_configs.labelElementGap;
    }

    // Only percent or only count shown
    return baseX + maxPercentWidth + _base_barchart_ui_configs.labelElementGap;
  };

  // ---------------------------------------------------------------------------
  // HOVER RECT GEOMETRY
  // ---------------------------------------------------------------------------
  const hoverRectX = _base_barchart_ui_configs.hoverRectStrokeWidth;
  const hoverRectWidth = `calc(100% - ${_base_barchart_ui_configs.hoverRectStrokeWidth * 2}px)`;

  // For grouped charts, extend hover area into gaps between bars for continuous tooltip
  // Each bar's hover rect extends half the gap above and below, so there are no "dead zones"
  const groupedGapExtension = _base_barchart_ui_configs.gapBetweenBarsInGroup / 2;

  // For grouped charts, use the `y` prop from Recharts which is the actual Y position of each individual bar
  // Subtract half the gap to extend the hover area upward into the gap above
  const hoverRectY = isGroupedChart
    ? (y as number) - groupedGapExtension
    : (!isBreakdown || showSumOfBreakdownValues)
      ? hoverRectX + CALC_SINGLE_BAR_SECTION_Y_POS((value as CountVizLabels).dataIndex)
      : breakdownDisplayType === 'stacked'
        ? hoverRectX + CALC_STACKED_BAR_SECTION_Y_POS((value[0] as CountVizLabels).dataIndex)
        : hoverRectX + (TOT_HEIGHT_GROUPED_BAR_SECTION + _base_barchart_ui_configs.gapBetweenBarSections) * (value as CountVizLabels[])[0].dataIndex;

  // For grouped charts, extend height to include the full gap (half above + half below)
  // This creates continuous hover detection with no gaps between bars
  const hoverRectHeight = isGroupedChart
    ? _base_barchart_ui_configs.barHeight + _base_barchart_ui_configs.gapBetweenBarsInGroup
    : (!isBreakdown || showSumOfBreakdownValues)
      ? _base_barchart_ui_configs.barHeight
      : breakdownDisplayType === 'stacked'
        ? TOT_HEIGHT_STACKED_BAR_SECTION
        : TOT_HEIGHT_GROUPED_BAR_SECTION;

  // ---------------------------------------------------------------------------
  // RENDER FUNCTIONS FOR DIFFERENT LABEL TYPES
  // ---------------------------------------------------------------------------

  /**
   * Renders the group label (category name) above grouped breakdown charts
   * Only shown on the first bar of each group
   */
  const renderGroupLabel = () => {
    if (!isGroupedChart || stackIndex !== 0 || !groupData || !Array.isArray(groupData)) {
      return null;
    }

    const groupName = groupData[0]?.name || '';

    return (
      <text
        opacity={opacity}
        x={_base_barchart_ui_configs.marginLeft}
        dy={(y as number) - (_base_barchart_ui_configs.groupedLabelToBarGap + _base_barchart_ui_configs.labelHeight)}
        fill={colors.grey80}
        textAnchor="start"
        className={LABEL_CSS_CLASSES.GROUP_LABEL}
      >
        {` ${groupName}`}
      </text>
    );
  };

  /**
   * Renders labels for STACKED breakdown charts
   * Format: [percent] [count] [name] all on one line above the bar
   */
  const renderStackedLabel = () => {
    return (
      <text
        opacity={opacity}
        x={stackedLabelX}
        dy={stackedLabelDy}
        fill={colors.grey80}
        textAnchor="start"
        className={LABEL_CSS_CLASSES.STACKED_LABEL}
      >
        {/* Percent or count value */}
        {(showPercent || showCount) && <tspan>{formatValue()}</tspan>}

        {/* Count (shown separately only if both showPercent and showCount are true) */}
        {showPercent && showCount && (
          <tspan
            className={LABEL_CSS_CLASSES.STACKED_LABEL}
            x={stackedLabelX + maxPercentWidth + _base_barchart_ui_configs.labelElementGap}
          >
            {getCount()}
          </tspan>
        )}

        {/* Category name */}
        <tspan
          ref={nameTspanRef}
          x={calculateLabelPosition(stackedLabelX)}
          fill={labelOverflow.overflowing ? `url(#${gradientId})` : colors.grey80}
        >
          {name}
        </tspan>
      </text>
    );
  };

  /**
   * Renders labels for SINGLE VALUE and GROUPED breakdown charts
   * Format: [percent] [count] [name] positioned below/beside the bar
   */
  const renderStandardLabel = () => {
    const shouldShowLabel = !(isBreakdown && !showSumOfBreakdownValues);

    return (
      <text
        opacity={opacity}
        x={mainLabelX}
        dy={mainLabelDy}
        fill={colors.grey70}
        textAnchor="start"
        className={LABEL_CSS_CLASSES.PERCENT_VALUE}
      >
        {/* Percent or count value */}
        {(showPercent || showCount) && <tspan>{formatValue()}</tspan>}

        {/* Count (shown separately only when showPercent is also true) */}
        {showPercent && showCount && (isGroupedChart || !isBreakdown || showSumOfBreakdownValues) && (
          <tspan
            className={LABEL_CSS_CLASSES.COUNT_VALUE}
            x={mainLabelX + maxPercentWidth + _base_barchart_ui_configs.labelElementGap}
            fill={colors.grey80}
          >
            {getCount()}
          </tspan>
        )}

        {/* Category name */}
        {shouldShowLabel && (
          <tspan
            ref={nameTspanRef}
            className={LABEL_CSS_CLASSES.LABEL_TEXT}
            x={calculateLabelPosition(mainLabelX)}
            fill={labelOverflow.overflowing ? `url(#${gradientId})` : colors.grey70}
          >
            {_Locale(name)}
          </tspan>
        )}
      </text>
    );
  };

  /**
   * Renders the interactive hover rectangle for mouse events
   */
  const renderHoverRect = () => {
    return (
      <rect
        style={onMouseDown ? { cursor: 'pointer' } : {}}
        onMouseDown={(e) => onMouseDown?.(e, value)}
        onMouseOver={(e) => {
          onMouseOver?.(e, value);
          // the rect sits above the label text, so label hover = row hover
          if (labelOverflow.overflowing) onOverflowLabelHover?.(name);
        }}
        onMouseOut={(e) => {
          onMouseOut?.(e, value);
          onOverflowLabelHover?.(null);
        }}
        x={hoverRectX}
        y={hoverRectY as number}
        width={hoverRectWidth}
        height={hoverRectHeight as number}
        opacity={
          _debug_controls.mouseEventDetectorOpacity ||
          (activeBar === name || isHighlighted ? 1 : 0)
        }
        stroke={isHighlighted ? colors.grey80 : colors.grey30}
        rx={5}
        ry={5}
        strokeWidth={`${isHighlighted ? 1.5 : _base_barchart_ui_configs.hoverRectStrokeWidth}px`}
        fillOpacity={_debug_controls.mouseEventDetectorOpacity}
      />
    );
  };

  return (
    <g>
      {/* Fade-out for an overflowing label: gradient anchored at the svg's
          right edge (userSpaceOnUse), solid until the last 30px — the SVG
          equivalent of BarGraphCollection's mask-image fade. */}
      {labelOverflow.overflowing && (
        <defs>
          <linearGradient
            id={gradientId}
            gradientUnits="userSpaceOnUse"
            x1={labelOverflow.svgWidth - 30}
            x2={labelOverflow.svgWidth}
            y1={0}
            y2={0}
          >
            <stop
              offset="0"
              stopColor={isStackedChart ? colors.grey80 : colors.grey70}
            />
            <stop
              offset="1"
              stopColor={isStackedChart ? colors.grey80 : colors.grey70}
              stopOpacity="0"
            />
          </linearGradient>
        </defs>
      )}

      {/* Group category label (only for grouped breakdown charts) */}
      {renderGroupLabel()}

      {/* Main label - render based on chart type */}
      {isStackedChart ? renderStackedLabel() : renderStandardLabel()}

      {/* Interactive hover rectangle */}
      {renderHoverRect()}
    </g>
  );
};

// =============================================================================
// MEMOIZED EXPORT
// =============================================================================
// We use React.memo with a custom comparison to optimize re-renders.
// Since the parent pre-computes value/groupData via useCallback, and event
// handlers are memoized, shallow comparison catches most changes efficiently.

/**
 * Custom comparison function for React.memo.
 *
 * OPTIMIZATION STRATEGY:
 * 1. Check primitives first (cheap comparisons)
 * 2. Reference equality for objects/functions (they're pre-computed/memoized)
 * 3. Skip comparing functions that are conditionally passed (onMouseOver, etc.)
 *    since they're either both undefined or both memoized references
 */
const arePropsEqual = (
  prevProps: CustomizedLabelProps,
  nextProps: CustomizedLabelProps
): boolean => {
  // 1. Quick bailout on primitive props that change frequently
  if (
    prevProps.y !== nextProps.y ||
    prevProps.activeBar !== nextProps.activeBar ||
    prevProps.highlightValue !== nextProps.highlightValue ||
    prevProps.opacity !== nextProps.opacity
  ) {
    return false;
  }

  // 2. Check display configuration (rarely changes after initial render)
  if (
    prevProps.showPercent !== nextProps.showPercent ||
    prevProps.showCount !== nextProps.showCount ||
    prevProps.showSumOfBreakdownValues !== nextProps.showSumOfBreakdownValues ||
    prevProps.breakdownDisplayType !== nextProps.breakdownDisplayType ||
    prevProps.stackIndex !== nextProps.stackIndex ||
    prevProps.totalDatasetCount !== nextProps.totalDatasetCount ||
    prevProps.maxPercentWidth !== nextProps.maxPercentWidth ||
    prevProps.maxCountWidth !== nextProps.maxCountWidth
  ) {
    return false;
  }

  // 3. Check pre-computed data props (reference equality - they're from useMemo/useCallback)
  if (
    prevProps.value !== nextProps.value ||
    prevProps.groupData !== nextProps.groupData ||
    prevProps.TOT_HEIGHT_GROUPED_BAR_SECTION !== nextProps.TOT_HEIGHT_GROUPED_BAR_SECTION
  ) {
    return false;
  }

  // 4. Config objects - reference equality (stable refs from parent)
  if (
    prevProps._base_barchart_ui_configs !== nextProps._base_barchart_ui_configs ||
    prevProps._debug_controls !== nextProps._debug_controls
  ) {
    return false;
  }

  // 5. Height/width from Recharts (may change on resize)
  if (
    prevProps.width !== nextProps.width ||
    prevProps.height !== nextProps.height
  ) {
    return false;
  }

  // All checks passed - props are equal, skip re-render
  return true;
};

export const CustomizedLabel = memo(CustomizedLabelInner, arePropsEqual);
